// Types + TanStack Query hook for the Top Performers momentum leaderboard.
//
// Auth / transport: we reuse the project's shared authenticated Axios instance
// (`apiClient` from src/api/client.ts). Its baseURL already includes the
// `/api/v1` version prefix and attaches the WordPress-issued JWT + the
// `forge_session` cookie on every request, and centralises the 401 -> silent
// refresh -> WP-login-bounce handling. Because `/api/v1` is baked into baseURL,
// we request the BARE `/top-performers/...` path here.
//
// The backend serves this entirely off the pre-computed `top_performers_ohlcv`
// hypertable (no live FMP call on the request path), so the round-trip is
// dominated by network latency, not compute. Field names are snake_case,
// matching the backend Pydantic contract exactly (no aliases).

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../../../api/client';

export type Market = 'US' | 'EU';

// Horizon keys the UI can rank on -- mirror the PerformerRow gain fields.
export type GainKey = 'gain_1d' | 'gain_7d' | 'gain_30d';

export interface PerformerRow {
    symbol: string;
    name: string | null;
    market: Market;
    // Backend-provided ISO listing currency (EUR for XETRA/Vienna, else USD).
    currency?: string | null;
    current_price: number;
    gain_1d: number | null;
    gain_7d: number | null;
    gain_30d: number | null;
    volatility: number | null;
    sparkline: number[];
}

export interface TopPerformersResponse {
    // US board (NYSE / NASDAQ, screened server-side).
    top_1d_us: PerformerRow[];
    worst_1d_us: PerformerRow[];
    top_7d_us: PerformerRow[];
    top_30d_us: PerformerRow[];

    // EU board (XETRA + Vienna, screened server-side).
    top_1d_eu: PerformerRow[];
    worst_1d_eu: PerformerRow[];
    top_7d_eu: PerformerRow[];
    top_30d_eu: PerformerRow[];

    generated_at: string; // ISO datetime
    universe_size: number;
    execution_time_ms: number;
}

const fetchTopPerformers = async (): Promise<TopPerformersResponse> => {
    const { data } = await apiClient.get<TopPerformersResponse>(
        '/top-performers/leaderboard',
    );
    return data;
};

export const useTopPerformers = () => {
    return useQuery({
        queryKey: ['top-performers', 'leaderboard'],
        queryFn: fetchTopPerformers,
        // The leaderboard is refreshed once daily by the ARQ worker, so a
        // 5-minute staleTime is safe and spares needless refetches.
        staleTime: 5 * 60 * 1000,
    });
};
